import { useState } from "react";
import { motion } from "framer-motion";
import { 
  Building2, 
  Scale, 
  Stethoscope,
  Users as UsersIcon,
  GraduationCap,
  Heart,
  CheckCircle,
  ArrowRight,
  FileText,
  Users,
  Clock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import SafetyBanner from "../components/SafetyBanner";
import { useLanguage, translations } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const partnerIcons = [Building2, Stethoscope, UsersIcon, Scale, GraduationCap, Heart];

const ReferralsPage = () => {
  const { language } = useLanguage();
  const t = translations[language].referrals;
  const tf = translations[language].forms;

  const [formData, setFormData] = useState({
    name: "",
    organization: "",
    organization_type: "",
    client_type: "",
    email: "",
    phone: "",
    notes: "",
    survivor_consent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.survivor_consent) {
      toast.error("Please confirm the survivor has consented to be contacted.");
      return;
    }
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/referrals`, formData);
      toast.success(t.successMessage);
      setFormData({
        name: "",
        organization: "",
        organization_type: "",
        client_type: "",
        email: "",
        phone: "",
        notes: "",
        survivor_consent: false,
      });
    } catch (error) {
      toast.error(t.errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <SafetyBanner />
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1766808982745-c3fc72ee4d88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxBc2lhbiUyMGZhbWlseSUyMHBhcmVudCUyMGNoaWxkJTIwYm9uZGluZyUyMGluZG9vcnxlbnwwfHx8fDE3NzU2ODQyMDZ8MA&ixlib=rb-4.1.0&q=85')` 
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-warm-700 mb-4 block">
              {t.heroOverline}
            </span>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight text-gray-900 mb-6"
              style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}
              data-testid="referrals-hero-title"
            >
              {t.heroTitle}
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-gray-600">
              {t.heroSubtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-warm-700 mb-4 block">
              {t.partnersOverline}
            </span>
            <h2 
              className="text-3xl sm:text-4xl font-semibold tracking-tight leading-snug text-gray-900 mb-4"
              style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}
            >
              {t.partnersTitle}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t.partnersSubtitle}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {t.partnerTypes.map((partner, idx) => {
              const PartnerIcon = partnerIcons[idx];
              return (
                <motion.div
                  key={partner.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white border border-gray-100 rounded-2xl p-8 text-center hover:shadow-md transition-all"
                  data-testid={`partner-type-${idx}`}
                >
                  <div className="w-16 h-16 bg-warm-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <PartnerIcon className="w-8 h-8 text-warm-700" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                    {partner.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {partner.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How to Refer */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-warm-700 mb-4 block">
                {t.processOverline}
              </span>
              <h2 
                className="text-3xl sm:text-4xl font-semibold tracking-tight leading-snug text-gray-900 mb-6"
                style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}
              >
                {t.processTitle}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                {t.processSubtitle}
              </p>

              <div className="space-y-6">
                {t.steps.map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="w-12 h-12 bg-warm-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-warm-700 font-bold text-sm">{item.step}</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                        {item.title}
                      </h4>
                      <p className="text-sm text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-warm-700 text-white rounded-2xl p-8"
            >
              <h3 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                {t.whyPartner}
              </h3>
              <div className="space-y-4 mb-8">
                {t.benefits.map((benefit) => (
                  <div key={benefit} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-warm-300 flex-shrink-0" />
                    <span className="text-warm-100">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-warm-600">
                <div className="text-center">
                  <FileText className="w-8 h-8 text-warm-300 mx-auto mb-2" />
                  <p className="text-xs text-warm-200">{t.documentation}</p>
                </div>
                <div className="text-center">
                  <Users className="w-8 h-8 text-warm-300 mx-auto mb-2" />
                  <p className="text-xs text-warm-200">{t.support}</p>
                </div>
                <div className="text-center">
                  <Clock className="w-8 h-8 text-warm-300 mx-auto mb-2" />
                  <p className="text-xs text-warm-200">{t.response}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Referral Form */}
      <section className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-warm-700 mb-4 block">
              {t.formOverline}
            </span>
            <h2 
              className="text-3xl sm:text-4xl font-semibold tracking-tight leading-snug text-gray-900 mb-4"
              style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}
            >
              {t.formTitle}
            </h2>
            <p className="text-lg text-gray-600">
              {t.formSubtitle}
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100"
          >
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-gray-700 mb-2 block">
                  {t.yourName} *
                </Label>
                <Input
                  id="name"
                  data-testid="referral-name-input"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="rounded-xl border-gray-200 focus:ring-warm-700 focus:border-warm-700"
                  placeholder={t.yourName}
                />
              </div>
              <div>
                <Label htmlFor="organization" className="text-sm font-medium text-gray-700 mb-2 block">
                  {t.organization} *
                </Label>
                <Input
                  id="organization"
                  data-testid="referral-org-input"
                  required
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                  className="rounded-xl border-gray-200 focus:ring-warm-700 focus:border-warm-700"
                  placeholder={t.organization}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <Label htmlFor="organization_type" className="text-sm font-medium text-gray-700 mb-2 block">
                  {t.organizationType} *
                </Label>
                <Select
                  value={formData.organization_type}
                  onValueChange={(value) => setFormData({ ...formData, organization_type: value })}
                >
                  <SelectTrigger data-testid="referral-org-type-select" className="rounded-xl border-gray-200">
                    <SelectValue placeholder={tf.selectOption} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cps">{t.orgTypes.cps}</SelectItem>
                    <SelectItem value="court">{t.orgTypes.court}</SelectItem>
                    <SelectItem value="hospital">{t.orgTypes.hospital}</SelectItem>
                    <SelectItem value="nonprofit">{t.orgTypes.nonprofit}</SelectItem>
                    <SelectItem value="attorney">{t.orgTypes.attorney}</SelectItem>
                    <SelectItem value="other">{t.orgTypes.other}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="client_type" className="text-sm font-medium text-gray-700 mb-2 block">
                  {t.clientNeeds} *
                </Label>
                <Select
                  value={formData.client_type}
                  onValueChange={(value) => setFormData({ ...formData, client_type: value })}
                >
                  <SelectTrigger data-testid="referral-client-type-select" className="rounded-xl border-gray-200">
                    <SelectValue placeholder={tf.selectOption} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parenting-classes">{t.clientTypes.parenting}</SelectItem>
                    <SelectItem value="violence-intervention">{t.clientTypes.violence}</SelectItem>
                    <SelectItem value="family-support">{t.clientTypes.family}</SelectItem>
                    <SelectItem value="multiple">{t.clientTypes.multiple}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-2 block">
                  {tf.email} *
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="referral-email-input"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="rounded-xl border-gray-200 focus:ring-warm-700 focus:border-warm-700"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700 mb-2 block">
                  {tf.phone} *
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  data-testid="referral-phone-input"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="rounded-xl border-gray-200 focus:ring-warm-700 focus:border-warm-700"
                  placeholder="(xxx) xxx-xxxx"
                />
              </div>
            </div>

            <div className="mb-6">
              <Label htmlFor="notes" className="text-sm font-medium text-gray-700 mb-2 block">
                {t.additionalNotes}
              </Label>
              <Textarea
                id="notes"
                data-testid="referral-notes-input"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="rounded-xl border-gray-200 focus:ring-warm-700 focus:border-warm-700 min-h-[120px]"
                placeholder={t.notesPlaceholder}
              />
            </div>

            {/* Survivor consent confirmation */}
            <label
              htmlFor="survivor-consent"
              className="flex items-start gap-3 bg-warm-50 border border-warm-100 rounded-xl p-4 mb-6 cursor-pointer"
              data-testid="referral-consent-wrap"
            >
              <input
                id="survivor-consent"
                type="checkbox"
                checked={formData.survivor_consent}
                onChange={(e) => setFormData({ ...formData, survivor_consent: e.target.checked })}
                className="mt-1 accent-warm-700"
                data-testid="referral-consent"
                required
              />
              <span className="text-sm text-gray-700">
                <span className="font-semibold text-gray-900">I confirm the survivor has agreed to be contacted by The Pillar Project.</span> If consent is unclear or the referrer is a community member submitting anonymously, please add context in the notes above and we will proceed with survivor-first outreach practices.
              </span>
            </label>

            <Button
              type="submit"
              data-testid="referral-submit-btn"
              disabled={isSubmitting || !formData.name || !formData.organization || !formData.organization_type || !formData.client_type || !formData.email || !formData.phone || !formData.survivor_consent}
              className="w-full bg-warm-700 hover:bg-warm-800 text-white rounded-full py-6"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {tf.sending}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {t.submitReferral}
                  <ArrowRight className="w-5 h-5" />
                </span>
              )}
            </Button>
          </motion.form>
        </div>
      </section>
    </div>
  );
};

export default ReferralsPage;
